import {
  ScopeType,
  type ScopedEnablements as ScopedEnablementsPayload,
} from '@portal/pkg-api/gen/shared'
import { Enablements } from './enablements'
import type { Scope } from './scope'

const NONE = new Enablements([])

/**
 * The caller's whole scope-aware answer set: the union across every scope they reach, plus what
 * they may do within each one.
 */
export class ScopedEnablements {
  private readonly unioned: Enablements
  private readonly byEntityType: ReadonlyMap<number, Enablements>

  constructor(payload: ScopedEnablementsPayload) {
    this.unioned = new Enablements(payload.enablements)
    this.byEntityType = new Map(
      payload.scopes
        .filter((scope) => scope.scopeType === ScopeType.ENTITY_TYPE)
        .map((scope) => [scope.scopeId, new Enablements(scope.enablements)]),
    )
  }

  static empty() {
    return new ScopedEnablements({ enablements: [], scopes: [] })
  }

  /** Across every scope the caller reaches. */
  global(): Enablements {
    return this.unioned
  }

  /**
   * Within one scope, or {@link global} without one. A scope absent from the payload is one the
   * caller cannot reach, so it answers no to everything rather than falling back to the union.
   */
  for(scope?: Scope): Enablements {
    if (!scope) {
      return this.unioned
    }
    return this.byEntityType.get(scope.entityTypeId) ?? NONE
  }

  /** Whether the payload has an entry for this scope, i.e. the caller reaches it. */
  knows(scope: Scope): boolean {
    return this.byEntityType.has(scope.entityTypeId)
  }
}
